/**
 * Integrations tab: catalog cards by category, Connect / Manage dialogs.
 */

import { useMemo, useState } from 'react'
import {
  CheckCircle2,
  CircleDashed,
  Loader2,
  PlugZap,
  Sparkles,
} from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { useIntegrationsCatalog } from '@/admin/hooks/use-integrations'
import type {
  CatalogEntry,
  IntegrationCategory,
} from '@/admin/types/integrations'

import { ConnectIntegrationDialog } from './connect-integration-dialog'
import { ManageIntegrationDialog } from './manage-integration-dialog'

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  address: 'Address Lookup',
  vehicle: 'Vehicle Lookup',
  erp: 'ERP & Accounting',
  payments: 'Payments',
  email: 'Email Delivery',
  sms: 'SMS & Notifications',
  ecommerce: 'E-Commerce',
  public_api: 'Public API',
}

function isSameCard(a: CatalogEntry, b: CatalogEntry): boolean {
  return a.source === b.source && a.provider === b.provider
}

function providerInitials(provider: string): string {
  if (provider.length <= 2) return provider.toUpperCase()
  const parts = provider.match(/[a-z]+/gi) ?? []
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return provider.slice(0, 2).toUpperCase()
}

function providerTint(provider: string): string {
  switch (provider) {
    case 'goaddress':
      return 'bg-[var(--green-bg)] text-[var(--green)]'
    case 'portalgo':
      return 'bg-[#e8f0fe] text-[#1a73e8]'
    case 'fetchify':
      return 'bg-[#fce8e8] text-[#d93025]'
    case 'dvla':
      return 'bg-[#fef3c7] text-[#92400e]'
    case 'sap':
      return 'bg-[#e8f0fe] text-[#0054a3]'
    case 'stripe':
      return 'bg-[#f0eaff] text-[#635bff]'
    case 'resend':
      return 'bg-[var(--bg-subtle)] text-[var(--ink)]'
    case 'twilio':
      return 'bg-[#fce8e8] text-[#cf272d]'
    case 'shopify':
      return 'bg-[#e3f4e8] text-[#008060]'
    case 'woocommerce':
      return 'bg-[#f0eaff] text-[#7f54b3]'
    case 'occonnect':
      return 'bg-[var(--green-bg)] text-[var(--green)]'
    default:
      return 'bg-[var(--bg-subtle)] text-[var(--ink-2)]'
  }
}

export function IntegrationsTab() {
  const { data: catalog, isLoading, isError } = useIntegrationsCatalog()

  const [connectProvider, setConnectProvider] = useState<CatalogEntry | null>(null)
  const [manageUuid, setManageUuid] = useState<string | null>(null)

  const grouped = useMemo(() => {
    const byCategory: Record<string, CatalogEntry[]> = {}
    for (const entry of catalog ?? []) {
      if (!byCategory[entry.category]) byCategory[entry.category] = []
      byCategory[entry.category].push(entry)
    }
    return byCategory
  }, [catalog])

  if (isLoading) {
    return (
      <div className="mt-10 flex items-center gap-2 text-[13px] text-[var(--ink-3)]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading integrations…
      </div>
    )
  }

  if (isError || !catalog) {
    return (
      <div className="mt-10 rounded-[6px] border-[0.5px] border-[var(--status-error-bg)] bg-[var(--status-error-bg)] px-4 py-3 text-[12px] text-[var(--status-error)]">
        Failed to load integrations. Refresh the page to try again.
      </div>
    )
  }

  return (
    <div className="mt-8 space-y-12">
      <header className="max-w-2xl">
        <h2 className="clara-heading mb-1">Integrations</h2>
        <p className="text-[13px] text-[var(--ink-3)]">
          Connect third-party services to your organisation. Credentials are
          encrypted at rest and only your organisation can use them.
        </p>
      </header>

      {Object.keys(grouped).length === 0 && (
        <p className="text-[13px] text-[var(--ink-3)]">
          No integrations are available for your organisation yet.
        </p>
      )}

      {Object.entries(grouped).map(([category, entries]) => {
        const activeEntry = entries.find((e) => e.connected) ?? null
        return (
          <section key={category} className="space-y-4">
            <h3 className="clara-section-label">
              {CATEGORY_LABELS[category as IntegrationCategory] ?? category}
            </h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {entries.map((entry) => (
                <ProviderCard
                  key={`${entry.source}:${entry.provider}`}
                  entry={entry}
                  activeSibling={
                    activeEntry && !isSameCard(activeEntry, entry)
                      ? activeEntry
                      : null
                  }
                  onConnect={() => setConnectProvider(entry)}
                  onManage={() => {
                    if (entry.integration_uuid) {
                      setManageUuid(entry.integration_uuid)
                    }
                  }}
                />
              ))}
            </div>
          </section>
        )
      })}

      {connectProvider && (
        <ConnectIntegrationDialog
          entry={connectProvider}
          activeSibling={
            (grouped[connectProvider.category] ?? []).find(
              (e) => e.connected && !isSameCard(e, connectProvider),
            ) ?? null
          }
          open={!!connectProvider}
          onClose={() => setConnectProvider(null)}
        />
      )}

      {manageUuid && (
        <ManageIntegrationDialog
          uuid={manageUuid}
          open={!!manageUuid}
          onClose={() => setManageUuid(null)}
        />
      )}
    </div>
  )
}

interface ProviderCardProps {
  entry: CatalogEntry
  /** Connected sibling in this category; Connect becomes a switch. */
  activeSibling: CatalogEntry | null
  onConnect: () => void
  onManage: () => void
}

function ProviderCard({
  entry,
  activeSibling,
  onConnect,
  onManage,
}: ProviderCardProps) {
  const canConnect = entry.available && !entry.connected && !entry.coming_soon
  const isSwitch = canConnect && !!activeSibling
  const isPlatform = entry.source === 'platform'

  const ring = entry.connected
    ? 'border-[var(--green)] border-[1px]'
    : isPlatform
      ? 'border-[var(--green)] border-[0.5px]'
      : 'border-[var(--rule)] border-[0.5px]'

  const dimmed = !entry.available ? 'opacity-60' : ''

  const surface = isPlatform ? 'bg-[var(--green-bg-subtle)]' : 'bg-[var(--bg-light)]'

  const footerCaption = entry.connected
    ? isPlatform
      ? 'Using company default'
      : 'Encrypted key stored'
    : entry.coming_soon
      ? 'On the roadmap'
      : isPlatform
        ? isSwitch
          ? `Will replace ${activeSibling.display_name}`
          : 'Included - no setup'
        : isSwitch
          ? `Will replace ${activeSibling.display_name}`
          : 'Requires API key'

  const ctaLabel = entry.connected
    ? 'Manage'
    : isPlatform
      ? isSwitch
        ? 'Use this instead'
        : 'Use this'
      : isSwitch
        ? 'Switch to'
        : 'Connect'

  return (
    <div
      className={`flex flex-col gap-4 rounded-[8px] p-5 transition-colors ${surface} ${ring} ${dimmed}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[6px] text-[13px] font-medium ${
              isPlatform
                ? 'bg-[var(--green)] text-[var(--bg)]'
                : providerTint(entry.provider)
            }`}
          >
            {isPlatform ? (
              <Sparkles className="h-4 w-4" />
            ) : (
              providerInitials(entry.provider)
            )}
          </div>
          <div>
            <div className="text-[14px] font-medium text-[var(--ink)]">
              {entry.display_name}
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              {isPlatform
                ? 'Included · no API key'
                : (CATEGORY_LABELS[entry.category] ?? entry.category)}
            </div>
          </div>
        </div>

        <StatusPill entry={entry} />
      </div>

      <p className="text-[12px] leading-[1.6] text-[var(--ink-2)]">
        {entry.description}
      </p>

      <div className="mt-auto flex items-center justify-between border-t-[0.5px] border-t-[var(--rule)] pt-3">
        <span className="text-[11px] text-[var(--ink-3)]">{footerCaption}</span>

        {entry.connected ? (
          <Button variant="outline" size="sm" onClick={onManage}>
            {ctaLabel}
          </Button>
        ) : canConnect ? (
          <Button size="sm" onClick={onConnect}>
            {isPlatform ? (
              <Sparkles className="h-3 w-3" />
            ) : (
              <PlugZap className="h-3 w-3" />
            )}
            {ctaLabel}
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled>
            {entry.coming_soon ? 'Coming Soon' : 'Unavailable'}
          </Button>
        )}
      </div>
    </div>
  )
}

function StatusPill({ entry }: { entry: CatalogEntry }) {
  if (entry.connected) {
    return (
      <Badge
        variant="secondary"
        className="gap-1 border-[0.5px] border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        <CheckCircle2 className="h-3 w-3" />
        Connected
      </Badge>
    )
  }
  if (entry.coming_soon) {
    return (
      <Badge
        variant="secondary"
        className="gap-1 bg-[var(--bg-subtle)] text-[var(--ink-3)]"
      >
        <CircleDashed className="h-3 w-3" />
        Coming Soon
      </Badge>
    )
  }
  if (entry.available) {
    if (entry.source === 'platform') {
      return (
        <Badge
          variant="secondary"
          className="gap-1 bg-[var(--green-bg)] text-[var(--green)]"
        >
          <Sparkles className="h-3 w-3" />
          Included
        </Badge>
      )
    }
    return (
      <Badge
        variant="secondary"
        className="bg-[var(--status-info-bg)] text-[var(--status-info)]"
      >
        Available
      </Badge>
    )
  }
  return (
    <Badge
      variant="secondary"
      className="bg-[var(--bg-subtle)] text-[var(--ink-3)]"
    >
      Unavailable
    </Badge>
  )
}
